import React, { useState, useEffect, useMemo } from 'react';
import { getMessage as getLocalMessage } from '../../services/offloadedMessageStore';
import { fmtDateTime, fmtDateTimeShort } from '@shared/formatDate';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Reply as ReplyIcon,
  ReplyAll as ReplyAllIcon,
  Forward as ForwardIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Cancel as CancelIcon,
  MoneyOff as RefundIcon,
  Timer as TimerIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { MessageDetail, UpdateSenderFeeRequest, UpdateCategoryFeeRequest, Transaction } from '../../types';
import messageService from '../../services/messageService';
import accountService from '../../services/accountService';
import api from '../../services/api';
import TransactionDetailsDialog from '../shared/TransactionDetailsDialog';
import { formatEmailAddress, getEmailDomainSync } from '../../services/systemConfigService';
import { sanitizeMessageHtml } from '../../utils/sanitizeMessageHtml';

// ── Message body local cache (30-day TTL, per-device) ────────────────────────
const MSG_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface MsgCacheEntry {
  body: string;
  subject: string;
  cachedAt: number;
}

function getMsgCache(mailId: string): string | null {
  try {
    const raw = localStorage.getItem(`msg_cache_${mailId}`);
    if (!raw) return null;
    const entry: MsgCacheEntry = JSON.parse(raw);
    if (Date.now() - entry.cachedAt > MSG_CACHE_TTL_MS) {
      localStorage.removeItem(`msg_cache_${mailId}`);
      return null;
    }
    return entry.body;
  } catch {
    return null;
  }
}

function setMsgCache(mailId: string, body: string, subject: string) {
  try {
    const entry: MsgCacheEntry = { body, subject, cachedAt: Date.now() };
    localStorage.setItem(`msg_cache_${mailId}`, JSON.stringify(entry));
  } catch {
    // Storage quota exceeded — skip caching silently
  }
}

const BODY_PLACEHOLDER_PREFIXES = [
  '⬇️ This message was offloaded',
  '[This message body is empty',
  'Message body empty by design',
];

// ─────────────────────────────────────────────────────────────────────────────

interface MessageViewerProps {
  mailId: string;
  folder?: 'inbox' | 'sent' | 'credits' | 'debits' | 'archive' | 'trash';
  onBack: () => void;
  onReply?: (message: MessageDetail) => void;
  onReplyAll?: (message: MessageDetail) => void;
  onForward?: (message: MessageDetail) => void;
  onDelete?: (mailId: string) => void;
}

const MessageViewer: React.FC<MessageViewerProps> = ({ mailId, folder, onBack, onReply, onReplyAll, onForward, onDelete }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [message, setMessage] = useState<MessageDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Sender fee dialog state
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [senderFee, setSenderFee] = useState<string>('0.05');
  const [senderRefuse, setSenderRefuse] = useState(false);
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeSuccess, setFeeSuccess] = useState<string | null>(null);

  // Transactions state (for sent AND inbox messages)
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [autoOpenedTab, setAutoOpenedTab] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Adjust sender postage dialog state
  const [adjustPostageOpen, setAdjustPostageOpen] = useState(false);
  const [newPostageRate, setNewPostageRate] = useState<string>('0.05');
  const [adjustPostageSaving, setAdjustPostageSaving] = useState(false);
  const [adjustPostageSuccess, setAdjustPostageSuccess] = useState<string | null>(null);
  
  // Adjust category rate dialog state (separate from sender adjustment)
  const [adjustCategoryOpen, setAdjustCategoryOpen] = useState(false);
  const [newCategoryRate, setNewCategoryRate] = useState<string>('0.05');
  const [adjustCategorySaving, setAdjustCategorySaving] = useState(false);

  useEffect(() => {
    loadMessage();
  }, [mailId, folder]);

  const sanitizedBodyHtml = useMemo(
    () => sanitizeMessageHtml(message?.body || ''),
    [message?.body]
  );

  const handleMessageBodyClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest('a');
    if (!anchor) return;

    const href = anchor.getAttribute('href') || '';
    if (!href.includes('identity-disclosure-requests')) return;

    event.preventDefault();
    const hashMatch = href.match(/#\/identity-disclosure-requests\/([^?#]+)(\?.*)?$/);
    const pathMatch = href.match(/\/identity-disclosure-requests\/([^?#]+)(\?.*)?$/);
    const requestId = hashMatch?.[1] || pathMatch?.[1];
    const query = hashMatch?.[2] || pathMatch?.[2] || '';
    if (requestId) {
      navigate(`/identity-disclosure-requests/${requestId}${query}`);
    }
  };

  const loadMessage = async () => {
    setLoading(true);
    setError(null);
    try {
      // Check device cache before hitting the API (blob decryption is expensive)
      const cachedBody = getMsgCache(mailId);

      const data = await messageService.getMessage(mailId);

      // Phase 3: If server says OFFLOADED, try to supply body from local IndexedDB
      if (data.body?.startsWith('⬇️ This message was offloaded')) {
        const local = await getLocalMessage(mailId).catch(() => null);
        if (local?.blobContent) {
          try {
            const resp = await api.post<{ plaintext: string }>('/api/offload/decrypt-local', {
              BlobContent: local.blobContent,
            });
            data.body = resp.plaintext;
          } catch { /* fall through to server placeholder */ }
        }
      }

      // Restore cached body when server returns a placeholder (blob re-download avoided)
      if (
        cachedBody &&
        !data.isSystemMessage &&
        (!data.body || BODY_PLACEHOLDER_PREFIXES.some(p => data.body!.startsWith(p)))
      ) {
        data.body = cachedBody;
      }

      // Cache the real body on this device for 30 days to avoid re-downloading blobs
      if (
        data.body &&
        !data.isSystemMessage &&
        !BODY_PLACEHOLDER_PREFIXES.some(p => data.body!.startsWith(p))
      ) {
        setMsgCache(mailId, data.body, data.subject || '');
      }

      setMessage(data);

      // Mark as read if not already
      if (!data.markRead) {
        await messageService.markAsRead(mailId);
        data.markRead = true;
        setMessage({ ...data });
      }

      // Load transactions for both sent AND inbox messages
      // For system messages (receipts), use sourceTransactionId if available for more reliable lookup
      let txns = await loadTransactions(data.sourceTransactionId);

      // Self-heal: if message is already read but has money fields and no transaction rows,
      // ask backend to reconcile transfer state, then reload transactions.
      const hasMonetaryValue = (data.attachedAmount ?? 0) > 0 || (data.deliveryFee ?? 0) > 0;
      if (data.markRead && hasMonetaryValue && (!txns || txns.length === 0)) {
        await messageService.markAsRead(mailId);
        txns = await loadTransactions(data.sourceTransactionId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load message');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (sourceTransactionId?: string): Promise<Transaction[]> => {
    try {
      // Use sourceTransactionId if available (for system messages like receipts)
      // This provides a direct lookup to the transaction ID rather than relying on OriginalId parsing
      const lookupId = sourceTransactionId || mailId;
      const txns = await accountService.getTransactionsByMailId(lookupId);
      console.log('📊 Loaded transactions for lookupId', lookupId, '(mailId:', mailId, '):', txns);
      setTransactions(txns);
      return txns ?? [];
    } catch (err: any) {
      console.error('Failed to load transactions:', err);
      // Don't show error to user, just silently fail
      return [];
    }
  };


  const formatFractionalCents = (amount: number) => {
    if (amount === 0) return '-';
    return `$${amount.toFixed(4)}`;
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await messageService.deleteMessage(mailId);
        if (onDelete) {
          onDelete(mailId);
        }
        onBack();
      } catch (err: any) {
        setError(err.message || 'Failed to delete message');
      }
    }
  };

  const handleArchive = async () => {
    try {
      await messageService.archiveMessage(mailId);
      onBack();
    } catch (err: any) {
      setError(err.message || 'Failed to archive message');
    }
  };

  const handleTrash = async () => {
    if (window.confirm('Move this message to trash? You can restore it from the trash folder.')) {
      try {
        await messageService.trashMessage(mailId);
        onBack();
      } catch (err: any) {
        setError(err.message || 'Failed to move message to trash');
      }
    }
  };

  const handleCancel = async () => {
    const retainedCount = message?.retainedCount ?? 0;
    const retainedNames = (message?.recipientStatuses ?? [])
      .filter((r: any) => r.retainsAccess)
      .map((r: any) => r.username)
      .join(', ');

    let confirmMsg = 'Recall this message?\n\nUnread recipients will lose access immediately.';
    if (retainedCount > 0) {
      confirmMsg += `\n\n⚠️ ${retainedCount} recipient(s) have already read this message and will RETAIN permanent access:\n${retainedNames}`;
    }
    confirmMsg += '\n\nYour funds will be refunded for unread copies.';

    if (window.confirm(confirmMsg)) {
      try {
        await messageService.cancelMessage(mailId);
        queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
        loadMessage();
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to recall message');
      }
    }
  };

  const handleExpireEarly = async () => {
    if (window.confirm('Expire this message for unread recipients? Already-read recipients keep their copy.')) {
      try {
        await messageService.expireMessage(mailId);
        
        // Invalidate balance cache to trigger refresh
        queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
        
        // Reload message to update state
        loadMessage();
      } catch (err: any) {
        setError(err.message || 'Failed to expire message');
      }
    }
  };

  const handleRefund = async () => {
    const reason = window.prompt('Enter reason for refund request (optional):');
    if (reason !== null) { // User didn't cancel
      try {
        await messageService.refundMessage(mailId, reason || undefined);
        // Reload message to update state
        loadMessage();
      } catch (err: any) {
        setError(err.message || 'Failed to request refund');
      }
    }
  };

  const handleOpenAdjustPostage = () => {
    // Pre-fill with actual postage received (from transaction), not max offered
    const currentPostage = transactions.length > 0 
      ? transactions[0]?.requiredPostage || 0.05 
      : 0.05;
    setNewPostageRate(currentPostage.toFixed(2));
    setAdjustPostageOpen(true);
  };

  const handleOpenAdjustCategory = () => {
    // Pre-fill with current postage as starting point for category rate
    const currentPostage = transactions.length > 0 
      ? transactions[0]?.requiredPostage || 0.05 
      : 0.05;
    setNewCategoryRate(currentPostage.toFixed(2));
    setAdjustCategoryOpen(true);
  };

  const handleSaveAdjustPostage = async () => {
    if (!message) return;
    
    const rate = parseFloat(newPostageRate);
    if (isNaN(rate) || rate < 0) {
      setError('Invalid postage rate');
      return;
    }
    
    setAdjustPostageSaving(true);
    try {
      // Use sender override endpoint - this goes to Sender-Specific Overrides, NOT address book
      const request: UpdateSenderFeeRequest = {
        senderAddress: message.fromAddress,
        deliveryFee: rate,
        refuseMail: false
      };
      
      await api.put('/api/commercialfees/sender', request);
      
      setAdjustPostageSuccess(`Sender rate updated to $${rate.toFixed(2)}`);
      setAdjustPostageOpen(false);
      setTimeout(() => setAdjustPostageSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to adjust sender postage rate');
    } finally {
      setAdjustPostageSaving(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId: number, fileName: string) => {
    try {
      // Use fetch directly for blob downloads to properly handle the response
      const token = api.getAccessToken() ?? localStorage.getItem('accessToken');
      const baseUrl = api.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/Attachment/${attachmentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Download failed:', err);
      setError('Failed to download attachment');
    }
  };

  const handleSaveAdjustCategory = async () => {
    if (!message) return;
    
    const rate = parseFloat(newCategoryRate);
    if (isNaN(rate) || rate < 0) {
      setError('Invalid category rate');
      return;
    }
    
    if (!message.commercialCode) {
      setError('No commercial category code found for this message');
      return;
    }
    
    setAdjustCategorySaving(true);
    try {
      // Use category fee endpoint - this updates the category-wide rate
      const request: UpdateCategoryFeeRequest = {
        commercialCode: message.commercialCode,
        deliveryFee: rate,
        refuseMail: false
      };
      
      await api.put('/api/commercialfees/category', request);
      
      setAdjustPostageSuccess(`Category rate updated to $${rate.toFixed(2)}`);
      setAdjustCategoryOpen(false);
      setTimeout(() => setAdjustPostageSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to adjust category rate');
    } finally {
      setAdjustCategorySaving(false);
    }
  };

  const handleSaveSenderFee = async () => {
    if (!message) return;
    
    const fee = parseFloat(senderFee);
    if (isNaN(fee) || fee < 0) {
      setError('Invalid fee value');
      return;
    }
    
    setFeeSaving(true);
    try {
      const request: UpdateSenderFeeRequest = {
        senderAddress: message.fromAddress,
        deliveryFee: fee,
        refuseMail: senderRefuse
      };
      await api.put('/api/commercialfees/sender', request);
      setFeeSuccess(senderRefuse 
        ? `All future messages from ${message.fromAddress} will be blocked` 
        : `Delivery fee for ${message.fromAddress} set to $${fee.toFixed(2)}`);
      setFeeDialogOpen(false);
      setTimeout(() => setFeeSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update sender fee');
    } finally {
      setFeeSaving(false);
    }
  };

  const formatDate = (dateString: string) => fmtDateTime(dateString);

  const getRecipientsByType = (type: number) => {
    // First try the structured recipients array
    const fromRecipients = message?.recipients?.filter(r => r.type === type) || [];
    if (fromRecipients.length > 0) return fromRecipients;
    
    // Fall back to flat string arrays from backend
    if (!message) return [];
    const msg = message as any;
    if (type === 0 && msg.toRecipients?.length > 0) {
      return msg.toRecipients.map((addr: string) => ({ emailAddress: addr, type: 0 }));
    }
    if (type === 1 && msg.ccRecipients?.length > 0) {
      return msg.ccRecipients.map((addr: string) => ({ emailAddress: addr, type: 1 }));
    }
    if (type === 2 && msg.bccRecipients?.length > 0) {
      return msg.bccRecipients.map((addr: string) => ({ emailAddress: addr, type: 2 }));
    }
    return [];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={onBack} sx={{ ml: 2 }}>
          Go Back
        </Button>
      </Alert>
    );
  }

  if (!message) {
    return null;
  }

  const toRecipients = getRecipientsByType(0);
  const ccRecipients = getRecipientsByType(1);
  const bccRecipients = getRecipientsByType(2); // Show BCC to sender viewing their own message

  // Determine if this is a sent message (user is the sender) or inbox message (user is recipient).
  // When the folder prop is provided, use it directly — this is the authoritative source and correctly
  // handles self-sent messages (where fromAddress contains the username even for inbox messages).
  const isSentMessage = folder
    ? folder === 'sent'
    : (message?.fromAddress?.toLowerCase().includes(user?.username?.toLowerCase() || '') ?? false);
  const isInboxMessage = !isSentMessage;
  const isIdentityDisclosureSystemNotice = isInboxMessage && (() => {
    const subject = (message?.subject || '').toLowerCase();
    const fromText = `${message?.fromName || ''} ${message?.fromAddress || ''}`.toLowerCase();
    const fromTrustHub = fromText.includes('qixit trust hub') || fromText.includes('system@');
    return subject.includes('id verification request') && fromTrustHub;
  })();
  
  // Use the backend-provided flag for system messages (Sales Receipt, Payment Receipt, etc.)
  // System messages don't have postage and shouldn't show rate adjustment options
  // IMPORTANT: Only consider it a system message if it's in the INBOX (not sent by user)
  const isSystemMessage = isInboxMessage && (
    (message as any)?.isSystemMessage || 
    message?.fromAddress?.toLowerCase() === `system@${getEmailDomainSync()}`
  ) || isIdentityDisclosureSystemNotice;

  // Get actual postage received from transaction (not the max offered by sender)
  const actualPostageReceived = transactions.length > 0 
    ? transactions[0]?.requiredPostage || 0 
    : 0;

  // Helper to extract username from email
  const extractUsername = (email?: string) => {
    if (!email) return 'Unknown';
    return email.split('@')[0];
  };

  // Helper to format recipients as usernames only
  const formatRecipientsShort = (recipients: any[]) => {
    return recipients.map(r => extractUsername(r.emailAddress)).join(', ');
  };

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Compact Header - 3 lines max */}
      <Box sx={{ p: { xs: 1, sm: 1.5 }, borderBottom: 1, borderColor: 'divider' }}>
        {/* Line 1: Back button, Subject, Action buttons */}
        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
          <IconButton onClick={onBack} size="small" sx={{ p: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {message.subject || '(No Subject)'}
          </Typography>
          
          {/* Action buttons - compact */}
          {message.canCancel && !message.isCanceled && (
            <Tooltip title="Cancel"><IconButton size="small" onClick={handleCancel} color="warning"><CancelIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {message.canExpireEarly && (
            <Tooltip title="Expire early"><IconButton size="small" onClick={handleExpireEarly} color="info"><TimerIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {message.canRefund && !message.isRefunded && message.deliveryFee > 0 && (
            <Tooltip title="Refund"><IconButton size="small" onClick={handleRefund} color="info"><RefundIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {onReply && !isSystemMessage && (
            <Tooltip title="Reply"><IconButton size="small" onClick={() => onReply(message)} color="primary"><ReplyIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {onReplyAll && !isSystemMessage && (
            <Tooltip title="Reply All"><IconButton size="small" onClick={() => onReplyAll(message)} color="primary"><ReplyAllIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {onForward && (
            <Tooltip title="Forward"><IconButton size="small" onClick={() => onForward(message)} color="primary"><ForwardIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {isInboxMessage && (
            <>
              <Tooltip title="Archive"><IconButton size="small" onClick={handleArchive}><ArchiveIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Trash"><IconButton size="small" onClick={handleTrash} color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </>
          )}
          {isSentMessage && onDelete && message.canDelete && (
            <Tooltip title="Delete"><IconButton size="small" onClick={handleDelete} color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          )}
        </Box>

        {/* Line 2 & 3: From/To, Date, Postage, Links - FOR ALL MESSAGES */}
        <>
          {/* Line 2: From | To | Date | Postage - System messages get simpler display */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ fontSize: '0.85rem' }}>
            {/* System Messages (Credits/Debits) - Show From for context */}
            {isSystemMessage && (
              <>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" color="text.secondary">From:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {message.fromName || extractUsername(message.fromAddress)}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" color="text.secondary">Date:</Typography>
                  <Typography variant="body2">{formatDate(message.sentDate)}</Typography>
                </Box>
                
                {/* Amount chip for system messages */}
                {message.attachedAmount > 0 && (
                  <Chip
                    label={`+$${message.attachedAmount.toFixed(2)}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      backgroundColor: '#e8f5e9',
                      color: '#2e7d32',
                      border: '1px solid #4caf50'
                    }}
                  />
                )}
              </>
            )}
            
            {/* Regular Messages */}
            {!isSystemMessage && (
              <>
                {/* From (for inbox) */}
                {isInboxMessage && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" color="text.secondary">From:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {message.fromName || extractUsername(message.fromAddress)}
                    </Typography>
                  </Box>
                )}
                
                {/* To (for sent) */}
                {isSentMessage && toRecipients.length > 0 && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" color="text.secondary">To:</Typography>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatRecipientsShort(toRecipients)}
                    </Typography>
                  </Box>
                )}
                
                {/* To (for inbox - show who it was sent to) */}
                {isInboxMessage && toRecipients.length > 0 && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" color="text.secondary">To:</Typography>
                    <Typography variant="body2">
                      {formatRecipientsShort(toRecipients)}
                    </Typography>
                  </Box>
                )}
                
                {/* Date */}
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" color="text.secondary">Date:</Typography>
                  <Typography variant="body2">{formatDate(message.sentDate)}</Typography>
                </Box>
                
                {/* Postage chip (for inbox - credit) - just the amount */}
                {/* Gold chip for commercial messages where advertiser paid fees */}
                {isInboxMessage && (
                  <Chip
                    label={`$${actualPostageReceived.toFixed(2)}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      backgroundColor: message.commercialMailing && message.feesPaidBySender ? '#fff8e1' : '#e8f5e9',
                      color: message.commercialMailing && message.feesPaidBySender ? '#f9a825' : '#2e7d32',
                      border: message.commercialMailing && message.feesPaidBySender ? '1px solid #ffc107' : '1px solid #4caf50'
                    }}
                  />
                )}
                
                {/* Attached funds chip (for inbox - credit) - black text to distinguish */}
                {/* Gold chip for commercial messages where advertiser paid fees */}
                {isInboxMessage && message.attachedAmount > 0 && (
                  <Chip
                    label={`$${message.attachedAmount.toFixed(2)}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      backgroundColor: message.commercialMailing && message.feesPaidBySender ? '#fff8e1' : '#e8f5e9',
                      color: '#1a1a1a', // Black text to distinguish from postage
                      border: message.commercialMailing && message.feesPaidBySender ? '1px solid #ffc107' : '1px solid #4caf50'
                    }}
                  />
                )}
                
                {/* URL Message Link - Permanent link for URL-based messages */}
                {message.browsingEnabled && message.contentUrl && (
                  <Link
                    href={message.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'primary.main',
                      textDecoration: 'none',
                      bgcolor: 'primary.50',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.100',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                    URL Message Link
                  </Link>
                )}
                
                {/* Postage chip (for sent - debit) - just the amount */}
                {isSentMessage && message.deliveryFee > 0 && (
                  <Chip
                    label={`-$${message.deliveryFee.toFixed(2)}`}
                    size="small"
                    color="error"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                
                {/* Attached funds chip (for sent - debit) - black text to distinguish */}
                {isSentMessage && message.attachedAmount > 0 && (
                  <Chip
                    label={`-$${message.attachedAmount.toFixed(2)}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      backgroundColor: '#ffebee',
                      color: '#1a1a1a', // Black text to distinguish from postage
                      border: '1px solid #ef5350'
                    }}
                  />
                )}
                
                {/* Attachments indicator */}
                {message.hasAttachments && (
                  <Chip
                    icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                    label={`${message.attachments.length}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                
                {/* Status for sent - show Delivered when message has been read by recipient */}
                {isSentMessage && (
                  <Chip 
                    label={message.isDelivered ? 'Read' : (message.statusDescription || 'Sent')} 
                    size="small" 
                    color={message.isDelivered ? 'success' : 
                           (message.statusDescription || '').includes('In Inbox') ? 'warning' : 'default'} 
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                
                {/* Per-recipient access status — shown when there are multiple recipients or after a recall */}
                {isSentMessage && (message.recipientStatuses ?? []).length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(message.recipientStatuses as any[]).map((rs: any, i: number) => (
                      <Chip
                        key={i}
                        size="small"
                        label={`${rs.username}: ${rs.hasRead ? '✓ Read' : (rs.status === 'C' ? '✗ Recalled' : '⏳ Unread')}`}
                        color={rs.hasRead ? 'success' : (rs.status === 'C' ? 'default' : 'warning')}
                        variant={rs.hasRead ? 'filled' : 'outlined'}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                        title={rs.retainsAccess ? 'This recipient retains permanent access (read before recall)' : ''}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Line 3: Links and additional recipient info */}
          <Box display="flex" alignItems="center" gap={2} mt={0.5} flexWrap="wrap">
            {/* For system messages - just show Details link */}
            {isSystemMessage && !isIdentityDisclosureSystemNotice && transactions.length > 0 && (
              <Link
                component="button"
                variant="caption"
                onClick={() => {
                  if (transactions.length > 0) {
                    setSelectedTransaction(transactions[0]);
                    setTransactionDialogOpen(true);
                  }
                }}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Details
              </Link>
            )}
            
            {/* For regular messages */}
            {!isSystemMessage && (
              <>
                {/* Adjust Sender Rate (inbox only) */}
                {isInboxMessage && (
                  <Link
                    component="button"
                    variant="caption"
                    onClick={handleOpenAdjustPostage}
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Adjust Sender Rate
                  </Link>
                )}
                
                {/* Commercial Category */}
                {message.commercialMailing && (
                  <>
                    <Chip
                      label={message.commercialCategory || "Marketing"}
                      size="small"
                      color="info"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    {isInboxMessage && (
                      <Link
                        component="button"
                        variant="caption"
                        onClick={handleOpenAdjustCategory}
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        Adjust Category Rate
                      </Link>
                    )}
                  </>
                )}
                
                {/* Details link - for inbox: single transaction, for sent: show table below */}
                {isInboxMessage && transactions.length > 0 && (
                  <Link
                    component="button"
                    variant="caption"
                    onClick={() => {
                      setSelectedTransaction(transactions[0]);
                      setTransactionDialogOpen(true);
                    }}
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Details
                  </Link>
                )}
                
                {/* CC/BCC indicators for sent messages */}
                {isSentMessage && ccRecipients.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    CC: {formatRecipientsShort(ccRecipients)}
                  </Typography>
                )}
                {isSentMessage && bccRecipients.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    BCC: {formatRecipientsShort(bccRecipients)}
                  </Typography>
                )}
              </>
            )}
          </Box>
          
          {/* Success message for fee update */}
          {feeSuccess && (
            <Alert severity="success" sx={{ mt: 1 }} onClose={() => setFeeSuccess(null)}>
              {feeSuccess}
            </Alert>
          )}
        </>
      </Box>

      {/* Transaction Details Table - For sent messages with recipients */}
      {(() => {
        console.log('📋 Table check:', {
          isSentMessage,
          isSystemMessage,
          transactionsLength: transactions.length,
          transactions,
          shouldShow: isSentMessage && !isSystemMessage && transactions.length > 0,
          toRecipients: toRecipients.map(r => r.emailAddress),
          ccRecipients: ccRecipients.map(r => r.emailAddress),
          bccRecipients: bccRecipients.map(r => r.emailAddress)
        });
        return null;
      })()}
      {isSentMessage && !isSystemMessage && transactions.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.50', py: 0.5, fontSize: '0.7rem' }}>Type</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', py: 0.5, fontSize: '0.7rem' }}>Recipient</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', py: 0.5, fontSize: '0.7rem' }}>Status</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'grey.50', py: 0.5, fontSize: '0.7rem' }}>Postage</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'grey.50', py: 0.5, fontSize: '0.7rem' }}>Attached</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'grey.50', py: 0.5, fontSize: '0.7rem' }}>Fees</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', py: 0.5, fontSize: '0.7rem' }}>Date/Time</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.50', py: 0.5, fontSize: '0.7rem' }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((txn, index) => {
                  // More thorough recipient type detection
                  const recipientEmail = formatEmailAddress(txn.toUser?.toLowerCase() || '');
                  
                  let recipientType = 'TO';
                  if (toRecipients.some(r => r.emailAddress?.toLowerCase() === recipientEmail)) {
                    recipientType = 'TO';
                  } else if (ccRecipients.some(r => r.emailAddress?.toLowerCase() === recipientEmail)) {
                    recipientType = 'CC';
                  } else if (bccRecipients.some(r => r.emailAddress?.toLowerCase() === recipientEmail)) {
                    recipientType = 'BCC';
                  }
                  
                  const statusColor = 
                    txn.action === 'Received' || txn.action === 'Delivered' ? 'success' :
                    txn.action === 'Pending' || txn.action === 'Sent' ? 'warning' :
                    txn.action === 'Expired' || txn.action === 'Rejected' ? 'error' : 'default';
                  
                  const postage = txn.requiredPostage ?? 0;
                  const attached = txn.attachedFunds ?? 0;
                  const fees = (txn.postageSystemFee ?? 0) + (txn.attachedFundsFee ?? 0);
                  
                  return (
                    <TableRow 
                      key={index}
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => {
                        setSelectedTransaction(txn);
                        setTransactionDialogOpen(true);
                      }}
                    >
                      <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>
                        <Chip 
                          label={recipientType} 
                          size="small" 
                          color={recipientType === 'TO' ? 'primary' : recipientType === 'CC' ? 'info' : 'default'}
                          sx={{ height: 18, fontSize: '0.65rem', minWidth: 38 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                        {txn.toUser || 'Unknown'}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>
                        <Chip 
                          label={txn.action || txn.type} 
                          size="small" 
                          color={statusColor as any}
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', color: postage > 0 ? 'error.main' : 'text.secondary' }}>
                        {postage > 0 ? `-$${postage.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', color: attached > 0 ? 'error.main' : 'text.secondary' }}>
                        {attached > 0 ? `-$${attached.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, fontSize: '0.7rem', color: fees > 0 ? 'error.main' : 'text.secondary' }}>
                        {fees > 0 ? `-$${fees.toFixed(4)}` : '-'}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>
                        {fmtDateTimeShort(txn.dateCompleted || txn.dateInitiated || null)}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>
                        <Link
                          component="span"
                          variant="caption"
                          sx={{ 
                            color: 'primary.main', 
                            textDecoration: 'none', 
                            '&:hover': { textDecoration: 'underline' },
                            cursor: 'pointer',
                            fontSize: '0.7rem'
                          }}
                        >
                          View
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Message Body */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {message.browsingEnabled && message.contentUrl ? (
          // Web Page mode - handle sites that block iframes
          iframeError ? (
            // Show clickable link when iframe is blocked (tab already opened automatically)
            <Box sx={{ 
              py: 4,
              px: 3,
              border: 1,
              borderColor: 'primary.main',
              borderRadius: 1,
              bgcolor: 'primary.50'
            }}>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                This website was opened in a new browser tab because it cannot be displayed in an embedded frame.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                If the tab didn't open automatically, click the link below:
              </Typography>
              <Link
                href={message.contentUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                <OpenInNewIcon />
                {message.contentUrl}
              </Link>
            </Box>
          ) : (
            // Try to display iframe
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              minHeight: '600px',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              position: 'relative'
            }}>
              <iframe
                src={message.contentUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '600px',
                  border: 'none',
                }}
                title="Message Content"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                onLoad={(e) => {
                  console.log('📄 Iframe onLoad event fired for:', message.contentUrl);
                  const iframe = e.target as HTMLIFrameElement;
                  
                  // Try to access iframe content to check if it's blocked
                  setTimeout(() => {
                    try {
                      // Try to access contentDocument or contentWindow.document
                      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                      
                      if (!iframeDoc) {
                        // Can't access content - likely blocked by X-Frame-Options
                        console.log('⚠️ Cannot access iframe content - blocked by security policy');
                        setIframeError(true);
                        if (!autoOpenedTab) {
                          console.log('🌐 Opening blocked URL in new tab');
                          window.open(message.contentUrl, '_blank', 'noopener,noreferrer');
                          setAutoOpenedTab(true);
                        }
                      } else {
                        // Successfully accessed content
                        console.log('✅ Iframe content accessible - displaying normally');
                        setIframeLoaded(true);
                      }
                    } catch (error) {
                      // Cross-origin error means content is blocked
                      console.log('⚠️ Cross-origin error accessing iframe:', error);
                      setIframeError(true);
                      if (!autoOpenedTab) {
                        console.log('🌐 Opening blocked URL in new tab');
                        window.open(message.contentUrl, '_blank', 'noopener,noreferrer');
                        setAutoOpenedTab(true);
                      }
                    }
                  }, 500); // Small delay to ensure iframe has fully loaded
                }}
                onError={() => {
                  console.log('⚠️ Iframe onError triggered:', message.contentUrl);
                  setIframeError(true);
                }}
              />
              {/* Always show "Open in New Tab" button as overlay */}
              <Box sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8, 
                zIndex: 1000 
              }}>
                <Tooltip title="Open in new tab">
                  <IconButton
                    size="small"
                    onClick={() => window.open(message.contentUrl, '_blank', 'noopener,noreferrer')}
                    sx={{ 
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'background.paper' }
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )
        ) : message.body ? (
          // Standard HTML body
          <div
            dangerouslySetInnerHTML={{ __html: sanitizedBodyHtml }}
            onClick={handleMessageBodyClick}
          />
        ) : (
          <Typography color="text.secondary" fontStyle="italic">
            (No content)
          </Typography>
        )}
      </Box>

      {/* Attachments */}
      {message.attachments && message.attachments.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Attachments ({message.attachments.length}):
            </Typography>
            {message.attachments.map((attachment) => (
              <Box
                key={attachment.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => handleDownloadAttachment(attachment.id, attachment.fileName)}
              >
                <AttachFileIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ flexGrow: 1, color: 'primary.main' }}>
                  {attachment.fileName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(attachment.fileSize / 1024).toFixed(2)} KB
                </Typography>
                <Tooltip title="Download">
                  <IconButton size="small" color="primary">
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Transaction Details table removed - use Details link in header instead */}

      {/* Transaction Details Dialog - Using shared component */}
      <TransactionDetailsDialog
        open={transactionDialogOpen}
        onClose={() => setTransactionDialogOpen(false)}
        transaction={selectedTransaction}
      />
      
      {/* Sender Fee Dialog (for commercial messages) */}
      <Dialog open={feeDialogOpen} onClose={() => setFeeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Set Delivery Fee for {message.fromName || message.fromAddress}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a custom delivery fee that will be required for all future messages from this sender.
            You can also choose to block all messages from this sender.
          </Typography>
          
          <TextField
            fullWidth
            label="Delivery Fee Required"
            type="number"
            value={senderFee}
            onChange={(e) => setSenderFee(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
            disabled={senderRefuse}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={senderRefuse} 
                onChange={(e) => setSenderRefuse(e.target.checked)}
                color="error"
              />
            }
            label="Block all future messages from this sender"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeeDialogOpen(false)} disabled={feeSaving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveSenderFee}
            disabled={feeSaving}
            color={senderRefuse ? 'error' : 'primary'}
          >
            {feeSaving ? <CircularProgress size={20} /> : (senderRefuse ? 'Block Sender' : 'Save Fee')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust Sender-Specific Postage Dialog (for inbox messages) */}
      <Dialog open={adjustPostageOpen} onClose={() => setAdjustPostageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          Adjust Sender-Specific Rate
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Set a custom postage rate for <strong>{message.fromName || message.fromAddress?.split('@')[0]}</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This message was delivered for <strong>${actualPostageReceived.toFixed(2)}</strong>.
            </Typography>
          </Alert>
          
          <TextField
            fullWidth
            label="Postage Required from This Sender"
            type="number"
            value={newPostageRate}
            onChange={(e) => setNewPostageRate(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
          />
          
          <Alert severity="warning" icon={<InfoIcon />}>
            <Typography variant="caption" component="div">
              <strong>Sender Rate Override:</strong>
            </Typography>
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              • This rate applies only to <strong>this specific sender</strong>
            </Typography>
            {message.commercialMailing && (
              <Typography variant="caption" component="div">
                • This will <strong>override</strong> the "{message.commercialCategory}" category rate for this sender
              </Typography>
            )}
            <Typography variant="caption" component="div">
              • The sender will be added to your address book if not already present
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustPostageOpen(false)} disabled={adjustPostageSaving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveAdjustPostage}
            disabled={adjustPostageSaving}
            color="primary"
          >
            {adjustPostageSaving ? <CircularProgress size={20} /> : 'Set Sender Rate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust Category Rate Dialog (for commercial messages) */}
      <Dialog open={adjustCategoryOpen} onClose={() => setAdjustCategoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="info" />
          Adjust "{message.commercialCategory || 'Marketing'}" Category Rate
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Set the default postage rate for <strong>all senders</strong> in the "{message.commercialCategory || 'Marketing'}" category.
            </Typography>
          </Alert>
          
          <TextField
            fullWidth
            label="Category Postage Rate"
            type="number"
            value={newCategoryRate}
            onChange={(e) => setNewCategoryRate(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
          />
          
          <Alert severity="warning" icon={<InfoIcon />}>
            <Typography variant="caption" component="div">
              <strong>Category Rate vs. Sender Rate:</strong>
            </Typography>
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              • This rate applies to <strong>all senders</strong> in the "{message.commercialCategory || 'Marketing'}" category
            </Typography>
            <Typography variant="caption" component="div">
              • If a sender is in your address book with a custom rate, their <strong>sender rate takes priority</strong>
            </Typography>
            <Typography variant="caption" component="div">
              • New senders in this category will use this rate until you set a sender-specific rate
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustCategoryOpen(false)} disabled={adjustCategorySaving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveAdjustCategory}
            disabled={adjustCategorySaving}
            color="info"
          >
            {adjustCategorySaving ? <CircularProgress size={20} /> : 'Set Category Rate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success message for postage adjustment */}
      {adjustPostageSuccess && (
        <Alert 
          severity="success" 
          sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }} 
          onClose={() => setAdjustPostageSuccess(null)}
        >
          {adjustPostageSuccess}
        </Alert>
      )}
    </Paper>
  );
};

export default MessageViewer;

